// Each endorsement.addon component renders an EndorsementCard with the appropriate endorsement info passed in as props.
// This component handles the shared logic of allowing users to select/deselect endorsements, showing/hiding endorsement details, and displaying invalid messages when certain endorsements cannot be added based on user answers.
import React, { useState, useImperativeHandle } from 'react';
import styled from 'styled-components';
import SmoothCollapse from 'react-smooth-collapse';
import Checkbox from '../../elements/Checkbox';
import { useThemeContext } from '../../elements/theme/CustomThemeProvider';
import { EndorsementCardContainer, EndorsementDescription, QuestionRow, InvalidMessage } from './styles/endorsements';
import { MoreInfoCaret } from '../Summary/AddOns/styles';
import { useSelector } from 'react-redux';
import { selectUserHasInvalidatedAddOns } from '../../selectors/application';

export const INVALID_MESSAGE_COPY =
  'Based on your answers, we\u2019re unable to offer this coverage. For help, please call our customer service at (855) 566 - 1011 or use our chat tool.';

export interface EndorsementCardHandle {
  setExpanded: (value: boolean) => void;
}

interface EndorsementCardProps {
  endorsementKey: string;
  label: string;
  description: React.ReactNode;
  isSelected: boolean;
  onCheckboxChange: (checked: boolean) => void;
  disabled?: boolean;
  hasExpandableContent?: boolean;
  hideCaret?: boolean;
  children?: React.ReactNode;
  expandedRef?: React.Ref<EndorsementCardHandle>;
}

const EndorsementCard = ({
  endorsementKey,
  label,
  description,
  isSelected,
  onCheckboxChange,
  disabled = false,
  hasExpandableContent = false,
  hideCaret = false,
  children,
  expandedRef,
}: EndorsementCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const invalidatedAddons = useSelector(selectUserHasInvalidatedAddOns) || [];
  const isInvalid = invalidatedAddons.includes(endorsementKey);

  const { theme } = useThemeContext();

  useImperativeHandle(expandedRef, () => ({
    setExpanded: (value: boolean) => setIsExpanded(value),
  }));

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      if (hasExpandableContent) {
        setIsExpanded(true);
      }
    } else {
      setIsExpanded(false);
    }
    onCheckboxChange(checked);
  };

  const handleToggleDetails = () => {
    setIsExpanded(!isExpanded);
  };

  const showCaret = hasExpandableContent && !hideCaret && !disabled && !isInvalid && isSelected;

  return (
    <EndorsementCardContainer
      isSelected={isSelected}
      disabled={disabled || isInvalid}
      data-testid={`${endorsementKey}-container`}
      data-cy={`${endorsementKey}-addOn`}
    >
      <QuestionRow data-testid="endorsement-question" data-cy={`${endorsementKey}-endorsement`}>
        {!isInvalid && !disabled ? (
          <Checkbox
            onChange={handleCheckboxChange}
            selected={isSelected}
            label={label}
            id={endorsementKey}
            showActiveLabel={true}
          />
        ) : (
          <EndorsementTitle>{label}</EndorsementTitle>
        )}
        {showCaret && (
          <MoreInfoButton
            onClick={handleToggleDetails}
            type="button"
            aria-label={`${isExpanded ? 'hide' : 'show'} ${endorsementKey} info`}
          >
            <MoreInfoCaret src={theme.icons.caret} expanded={isExpanded} />
          </MoreInfoButton>
        )}
      </QuestionRow>
      {isInvalid ? (
        <InvalidMessage>{INVALID_MESSAGE_COPY}</InvalidMessage>
      ) : (
        <EndorsementDescription disabled={disabled}>{description}</EndorsementDescription>
      )}
      {hasExpandableContent && (
        <SmoothCollapse heightTransition=".1s ease-out" expanded={isExpanded} allowOverflowWhenOpen={true}>
          <StyledHeaderRow />
          {children}
        </SmoothCollapse>
      )}
    </EndorsementCardContainer>
  );
};

const EndorsementTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  font-family: ${(props) => props.theme.font.typeface.primary};
  color: ${(props) => props.theme.colors.tertiary.zero};
  margin-top: 0;
  margin-bottom: 8px;
`;

const MoreInfoButton = styled.button`
  background: transparent;
  border: none;
  height: 25px;
`;

const StyledHeaderRow = styled.hr`
  border: 0.5px solid #e7e4e4;
  margin-bottom: 16px;
`;

export default EndorsementCard;
